import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Clock, Building2, Users, Calendar } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_showtime-staff/artifacts/1ijkahsm_logo%20jf.png";

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-purple-50 flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-purple-600 via-purple-700 to-violet-800 p-12 flex-col justify-between">
        <div>
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="StaffEase Logo" className="w-14 h-14 object-contain bg-white rounded-xl p-1" />
            <span className="text-3xl font-bold text-white">STP TeamBase</span>
          </div>
          <p className="mt-6 text-purple-100 text-lg max-w-md">
            The all-in-one employee management platform for scheduling, time tracking, and payroll.
          </p>
        </div>
        
        <div className="space-y-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <Calendar className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-lg">Smart Scheduling</h3>
              <p className="text-purple-200">Build schedules in minutes with drag-and-drop simplicity</p>
            </div>
          </div>
          
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <Clock className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-lg">Time Tracking</h3>
              <p className="text-purple-200">Track hours, breaks, and overtime automatically</p>
            </div>
          </div>
          
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-lg">Multi-Location</h3>
              <p className="text-purple-200">Manage all your locations from one dashboard</p>
            </div>
          </div>
          
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-lg">Team Management</h3>
              <p className="text-purple-200">Keep your entire team organized and informed</p>
            </div>
          </div>
        </div>
        
        <div className="text-purple-200 text-sm">
          Trusted by 150,000+ businesses worldwide
        </div>
      </div>
      
      {/* Right side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <Card className="w-full max-w-md shadow-xl border-0">
          <CardHeader className="space-y-1 pb-6">
            <div className="lg:hidden flex items-center gap-2 mb-4">
              <img src={LOGO_URL} alt="StaffEase Logo" className="w-10 h-10 object-contain" />
              <span className="text-2xl font-bold text-purple-900">STP TeamBase</span>
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900">Welcome back</CardTitle>
            <CardDescription className="text-gray-500">
              Sign in to access your dashboard
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-700">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-11 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-700">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-11 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>
              
              <Button
                type="submit"
                className="w-full h-11 bg-purple-600 hover:bg-purple-700 text-white font-medium"
                disabled={isLoading}
              >
                {isLoading ? 'Signing in...' : 'Sign in'}
              </Button>
            </form>
            
            <div className="mt-8 pt-6 border-t border-gray-200 space-y-3">
              <div className="p-3 bg-violet-50 rounded-lg">
                <p className="text-sm text-purple-700">
                  <strong>Employee?</strong> <a href="/employee" className="underline font-medium" data-testid="employee-signin-link">Employee Sign In Here</a>
                </p>
              </div>
              <div className="p-3 bg-violet-50 rounded-lg">
                <p className="text-sm text-purple-700">
                  <strong>Clock In/Out Kiosk:</strong> Visit <a href="/clock" className="underline font-medium">/clock</a> for the dedicated tablet clock-in interface (no login required)
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;
